import { keyframes, style, styleVariants } from "@vanilla-extract/css";
import { recipe } from "@vanilla-extract/recipes";
import { breakpoints, fontVariantsMap, vars } from "../../styles";

// Right slide animations (for larger screens)
const slideInRight = keyframes({
  from: { transform: "translateX(100%)" },
  to: { transform: "translateX(0)" },
});

const slideOutRight = keyframes({
  from: { transform: "translateX(0)" },
  to: { transform: "translateX(100%)" },
});

// Left slide animations (for RTL on larger screens)
const slideInLeft = keyframes({
  from: { transform: "translateX(-100%)" },
  to: { transform: "translateX(0)" },
});

const slideOutLeft = keyframes({
  from: { transform: "translateX(0)" },
  to: { transform: "translateX(-100%)" },
});

// Bottom slide animations (for smaller screens)
const slideInBottom = keyframes({
  from: { transform: "translateY(100%)" },
  to: { transform: "translateY(0)" },
});

const slideOutBottom = keyframes({
  from: { transform: "translateY(0)" },
  to: { transform: "translateY(100%)" },
});

const sheetBaseStyles = style({
  position: "fixed",
  top: "auto",
  bottom: 0,
  insetInlineStart: 0,
  insetInlineEnd: 0,
  transition: "width 150ms ease-in-out",
  willChange: "transform",
  border: "none",
  borderRadius: `${vars.radii.l} ${vars.radii.l} 0 0`,
  backgroundColor: vars.colors.surfaceContainer,
  padding: 0,
  width: "100%",
  maxWidth: "100vw",
  overflow: "hidden",
  selectors: {
    "[data-entering] &": {
      animation: `${slideInBottom} 200ms ease-in-out forwards`,

      "@media": {
        [`screen and (min-width:${breakpoints[4]})`]: {
          animation: `${slideInRight} 200ms ease-in-out forwards`,
        },
      },
    },
    '[dir="rtl"] [data-entering] &': {
      "@media": {
        [`screen and (min-width:${breakpoints[4]})`]: {
          animation: `${slideInLeft} 200ms ease-in-out forwards`,
        },
      },
    },
    "[data-exiting] &": {
      animation: `${slideOutBottom} 200ms ease-in-out forwards`,
      "@media": {
        [`screen and (min-width:${breakpoints[4]})`]: {
          animation: `${slideOutRight} 200ms ease-in-out forwards`,
        },
      },
    },
    '[dir="rtl"] [data-exiting] &': {
      "@media": {
        [`screen and (min-width:${breakpoints[4]})`]: {
          animation: `${slideOutLeft} 200ms ease-in-out forwards`,
        },
      },
    },
    "[data-focused] &": {
      outline: "none",
    },
    "[data-focus-visible] &": {
      outline: "auto",
    },
  },
  "@media": {
    [`screen and (min-width:${breakpoints[4]})`]: {
      insetInlineStart: "auto",
      insetInlineEnd: vars.space.m,
      marginBlock: vars.space.m,
      borderEndStartRadius: vars.radii.l,
      borderEndEndRadius: vars.radii.l,
    },
    [`screen and (min-width: ${breakpoints[8]})`]: {
      insetInlineStart: "auto",
      insetInlineEnd: "15%",
    },
    "(prefers-reduced-motion: reduce)": {
      selectors: {
        "[data-entering] &, [data-exiting] &": {
          animation: "none !important",
        },
      },
    },
  },
});

export const modalStyleVariants = styleVariants({
  small: [
    sheetBaseStyles,
    {
      "@media": {
        [`screen and (min-width:${breakpoints[4]})`]: { width: 375 },
      },
    },
  ],
  large: [
    sheetBaseStyles,
    {
      "@media": {
        [`screen and (min-width:${breakpoints[4]})`]: { width: "50vw" },
      },
    },
  ],
});

export const headerSectionStyles = style({
  boxSizing: "border-box",
  zIndex: vars.zIndex.table.stickyHead,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  marginBottom: vars.space["2xs"],
  backgroundColor: vars.colors.surfaceContainer,
  padding: `${vars.space.xs} ${vars.space.m} 0 ${vars.space.m}`,
  width: "100%",
});

export const actionBarStyles = style({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingBottom: vars.space.s,
});

export const headingStyles = recipe({
  base: {
    margin: 0,
    marginBottom: vars.space.m,
    color: vars.colors.onSurface,
    fontFamily: vars.fonts.bold,
  },
  variants: {
    size: fontVariantsMap,
  },
});

export const closeButtonWrapper = style({
  marginInlineStart: "auto",
  padding: `${vars.space["2xs"]} 0`,
  color: vars.colors.onSurface,
});

export const footerActionBarStyles = style({
  gap: vars.space.xs,
  backgroundColor: vars.colors.surfaceContainer,
  padding: vars.space.m,
});

export const dialogWrapperStyles = style({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  justifyContent: "space-between",
  maxHeight: "90dvh",
  overflow: "hidden",
  "@media": {
    [`screen and (min-width:${breakpoints[4]})`]: {
      height: `calc(100vh - ${vars.space.m} * 2)`,
      maxHeight: `calc(100vh - ${vars.space.m} * 2)`,
    },
  },
});

export const contentStyles = style({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  overflowY: "auto",
});
